import {
  type LegacyRef,
  type RefObject,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import type { DraggableBounds } from "react-draggable";

interface DraggableRefsAndBounds<T extends HTMLElement> {
  draggableRef: RefObject<T | null>;
  contentRef: LegacyRef<T>;
  draggableBounds: DraggableBounds | undefined;
  updateBounds: () => void;
}

/**
 * This hook has been inspired by:
 * https://tkdodo.eu/blog/avoiding-use-effect-with-callback-refs
 */
export default function useDraggableRefsAndBounds<T extends HTMLElement>(
  anchorElement: HTMLElement | null,
): DraggableRefsAndBounds<T> {
  const draggableRef = useRef<T | null>(null);
  const [mustComputeBounds, setMustComputeBounds] = useState(false);
  const [draggableBounds, setDraggableBounds] = useState<
    DraggableBounds | undefined
  >(undefined);

  const contentRef = useCallback(
    (node: T | null) => {
      if (node !== null && anchorElement !== null) {
        setMustComputeBounds(true);
      }
      draggableRef.current = node;
    },
    [anchorElement],
  );

  useEffect(() => {
    if (!mustComputeBounds) return;
    setMustComputeBounds(false);
    if (anchorElement !== null && draggableRef.current !== null) {
      const nodeRect = draggableRef.current.getBoundingClientRect();
      const anchorRect = anchorElement.getBoundingClientRect();
      setDraggableBounds({
        left: -anchorRect.left + 100,
        right: window.innerWidth - anchorRect.left - nodeRect.width,
        top: -anchorRect.top - anchorRect.height,
        bottom: window.innerHeight - anchorRect.bottom - nodeRect.height,
      });
    }
  }, [mustComputeBounds, anchorElement]);
  return {
    draggableRef,
    contentRef,
    draggableBounds,
    updateBounds: (): void => {
      setMustComputeBounds(true);
    },
  };
}
